import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import TextField from '@mui/material/TextField';
import SettingsSection from './SettingsSection';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import QuantityInput from './QuantityInput';
import LocationSelect, { LocationValue, EMPTY_LOCATION } from './LocationSelect';
import MappingList, { InPlaceEditor } from './MappingList';
import { useApiData, LoadError, Loading } from './useApiData';
import {
    apiGetSwishSettings,
    apiSetSwishPosMessage, apiPutSwishCategory, apiDeleteSwishCategory,
    apiPutSwishMessage, apiDeleteSwishMessage,
    apiPutSwishDefault, apiDeleteSwishDefault, swishPosterUrl,
    apiPutSwishGiftcardCost, apiDeleteSwishGiftcardCost,
} from './Api';
import {
    SwishSettingsData, SwishLocation, SwishCategoryMapping, SwishMessageMapping, SwishAmountMode,
    SwishCostLocation,
} from './Types';
import { messageStrength } from './messageStrength';
import { useScopeKeys, scopeKey } from './Scope';
import { useHasCapability } from './Capabilities';

const AMOUNT_MODES: { value: SwishAmountMode, label: string }[] = [
    { value: 'open', label: 'Öppet – betalaren fyller i beloppet' },
    { value: 'fixed', label: 'Fast – låst belopp' },
    { value: 'prefilled', label: 'Förifyllt – men kan ändras' },
]
const amountLabel = (m: SwishMessageMapping): string => {
    const amount = m.amount_mode === 'fixed' ? `${m.amount} kr (fast)`
        : m.amount_mode === 'prefilled' ? `${m.amount} kr (förifyllt)`
        : 'valfritt belopp'
    // Visible in the list too: which poster stands in for the POS changes how a day reads.
    return m.is_zettle_backup ? `${amount} · reserv för Zettle` : amount
}

const toLocation = (p: LocationValue): SwishLocation | null =>
    (p.cost_center_item_id !== null && p.project_id !== null && p.account !== null)
        ? { cost_center_item_id: p.cost_center_item_id, project_id: p.project_id, income_account: p.account }
        : null
const fromLocation = (l: SwishLocation | null): LocationValue =>
    l === null ? EMPTY_LOCATION : { cost_center_item_id: l.cost_center_item_id, project_id: l.project_id, account: l.income_account }

// A saved location as a human one-liner (account · kostnadsställe · projekt) for the WYSIWYG list.
// Cost-centre/project names come from the complete maps (not the picker tree), so a stored
// mapping outside the active role's scope still shows names rather than raw GUIDs.
const summarizeLocation = (data: SwishSettingsData, loc: SwishLocation): string => {
    const acct = data.official_income_accounts.find(a => a.account_number === loc.income_account)
    const acctText = acct ? `${acct.account_number} ${acct.account_name}` : String(loc.income_account)
    const ccName = data.cost_center_names[loc.cost_center_item_id] ?? loc.cost_center_item_id
    const projName = data.project_names[loc.project_id] ?? loc.project_id
    return `${acctText} · ${ccName} · ${projName}`
}

const amberWarning = { color: '#b26a00', fontSize: '0.75rem', maxWidth: '20rem', marginTop: '0.25rem' } as const

// The shared three-in-one selector (LocationSelect) bound to income data: a project exposes its
// income accounts, named "<number> – <name>" from the official income-account list. `required`
// (the LocationSelect default) so a partially- or un-filled location is marked red, like every
// other required input in the app — the three parts of a location are required together.
function IncomeLocation({ incomeData, value, onChange, disabled }: {
    incomeData: SwishSettingsData, value: LocationValue, onChange: (next: LocationValue) => void, disabled?: boolean,
}) {
    const accountName = (n: number) => {
        const a = incomeData.official_income_accounts.find(a => a.account_number === n)
        return a ? `${a.account_number} – ${a.account_name}` : String(n)
    }
    return (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <LocationSelect costCenterItems={incomeData.cost_center_items}
                accountNumbersOf={p => p.income_account_numbers} accountName={accountName}
                value={value} onChange={onChange} disabled={disabled}/>
        </div>
    )
}

// Pick-a-location editor in the in-place dialog shell. Reused by the category mapping and the
// default-placement editors — both are just "choose a location" (plus an optional heading).
function LocationOnlyEditor({ incomeData, heading, initialLoc, disabled, onCancel, onSave }: {
    incomeData: SwishSettingsData, heading?: React.ReactNode, initialLoc: LocationValue, disabled: boolean,
    onCancel: () => void, onSave: (loc: SwishLocation) => void,
}) {
    const [loc, setLoc] = useState<LocationValue>(initialLoc)
    const complete = toLocation(loc)
    return (
        <InPlaceEditor disabled={disabled} canSave={complete !== null} onCancel={onCancel}
            onSave={() => { if (complete !== null) onSave(complete) }}>
            {heading}
            <IncomeLocation incomeData={incomeData} value={loc} onChange={setLoc} disabled={disabled}/>
        </InPlaceEditor>
    )
}

// The presentkort cost as a one-liner, like summarizeLocation but over the expense accounts.
const summarizeCostLocation = (data: SwishSettingsData, loc: SwishCostLocation): string => {
    const name = data.expense_accounts[loc.expense_account]
    const acctText = name && name !== String(loc.expense_account) ? `${loc.expense_account} ${name}` : String(loc.expense_account)
    const ccName = data.cost_center_names[loc.cost_center_item_id] ?? loc.cost_center_item_id
    const projName = data.project_names[loc.project_id] ?? loc.project_id
    return `${acctText} · ${ccName} · ${projName}`
}

// Pick where a redeemed presentkort's cost lands. Same three-in-one selector as the income
// mappings, but over the expense-account tree: the cost is an expense, booked like a supplier
// invoice row would be.
function CostLocationEditor({ data, initial, disabled, onCancel, onSave }: {
    data: SwishSettingsData, initial: SwishCostLocation | null, disabled: boolean,
    onCancel: () => void, onSave: (loc: SwishCostLocation) => void,
}) {
    const [loc, setLoc] = useState<LocationValue>(initial === null ? EMPTY_LOCATION
        : { cost_center_item_id: initial.cost_center_item_id, project_id: initial.project_id, account: initial.expense_account })
    const complete: SwishCostLocation | null =
        (loc.cost_center_item_id !== null && loc.project_id !== null && loc.account !== null)
            ? { cost_center_item_id: loc.cost_center_item_id, project_id: loc.project_id, expense_account: loc.account }
            : null
    const accountName = (n: number) => {
        const name = data.expense_accounts[n]
        return name && name !== String(n) ? `${n} – ${name}` : String(n)
    }
    return (
        <InPlaceEditor disabled={disabled} canSave={complete !== null} onCancel={onCancel}
            onSave={() => { if (complete !== null) onSave(complete) }}>
            <div style={{ fontWeight: 600 }}>Kostnad för inlösta presentkort</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <LocationSelect costCenterItems={data.expense_cost_center_items}
                    accountNumbersOf={p => p.expense_account_numbers} accountName={accountName}
                    value={loc} onChange={setLoc} disabled={disabled}/>
            </div>
        </InPlaceEditor>
    )
}

// Message add/edit editor (message field + location) in the in-place dialog shell.
function MessageEditor({ incomeData, initialMessage, initialLoc, initialAmountMode, initialAmount, initialIsBackup, disabled, onCancel, onSave }: {
    incomeData: SwishSettingsData, initialMessage: string, initialLoc: LocationValue,
    initialAmountMode: SwishAmountMode, initialAmount: number | null, initialIsBackup: boolean,
    disabled: boolean,
    onCancel: () => void,
    onSave: (message: string, loc: SwishLocation, amountMode: SwishAmountMode, amount: number | null,
             isBackup: boolean) => void,
}) {
    const [message, setMessage] = useState(initialMessage)
    const [loc, setLoc] = useState<LocationValue>(initialLoc)
    const [amountMode, setAmountMode] = useState<SwishAmountMode>(initialAmountMode)
    const [amount, setAmount] = useState<number | null>(initialAmount)
    const [isBackup, setIsBackup] = useState(initialIsBackup)
    const complete = toLocation(loc)
    const warning = messageStrength(message)
    const needsAmount = amountMode !== 'open'
    const amountOk = !needsAmount || (amount !== null && amount > 0)
    return (
        <InPlaceEditor disabled={disabled} canSave={message.trim() !== '' && complete !== null && amountOk}
            onCancel={onCancel}
            onSave={() => { if (complete !== null && amountOk) onSave(message.trim(), complete, amountMode, needsAmount ? amount : null, isBackup) }}>
            <div>
                <TextInput label="Meddelande" value={message} disabled={disabled} onChange={setMessage}/>
                {message.trim() !== '' && warning.weak && <div style={amberWarning}>⚠ {warning.reason}</div>}
            </div>
            <IncomeLocation incomeData={incomeData} value={loc} onChange={setLoc} disabled={disabled}/>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <SelectInput<SwishAmountMode, { value: SwishAmountMode, label: string }, never>
                    label="Belopp på affischen" options={AMOUNT_MODES}
                    valueGetter={o => o.value} nameGetter={o => o.label}
                    value={amountMode} required={true} disabled={disabled} onChange={setAmountMode}/>
                {needsAmount &&
                    <QuantityInput label="Belopp (kr)" value={amount} disabled={disabled} onChange={setAmount}/>}
            </div>
            <div>
                {/* The treasurer's declaration that this poster stands in for the POS. Eko never
                    infers it: without it, a Zettle sale keyed in after an outage is reported as
                    money that never arrived, which is what it looks like on the data alone. */}
                <FormControlLabel
                    control={<Checkbox size="small" checked={isBackup} disabled={disabled}
                                       onChange={() => setIsBackup(v => !v)}/>}
                    label="Reserv för Zettle"/>
                <div style={{ color: '#555', fontSize: '0.85em', marginTop: '-0.3rem' }}>
                    Kryssa i om affischen används när Zettle-kassan ligger nere. Ett köp som
                    knappas in i Zettle i efterhand markeras som ”Täcks av reservaffisch” och inte
                    som obetalt om varje del av köpet betalats på en reservaffisch som bokförs på
                    samma konto, kostnadsställe och projekt.
                </div>
            </div>
        </InPlaceEditor>
    )
}

const SwishSettings = () => {
    const settings = useApiData(apiGetSwishSettings, [])
    const [busy, setBusy] = useState(false)
    // A mapping is editable/removable only if its location is in the active role's scope (you see
    // every mapping, but only touch your own). null scope keys ⇒ no provider ⇒ no restriction.
    const scopeKeys = useScopeKeys()
    const inScope = (loc: { cost_center_item_id: string, project_id: string }) =>
        scopeKeys === null || scopeKeys.has(scopeKey(loc.cost_center_item_id, loc.project_id))
    const canCategories = useHasCapability('config.swish_categories')
    const canDefault = useHasCapability('config.swish_default')
    const canMessages = useHasCapability('config.swish_messages')

    const data = settings.data

    // Run an API mutation, then re-pull settings so the view reflects the stored truth. Returns
    // the promise so a caller can close its editor once the save + reload has completed.
    const run = async (action: () => Promise<unknown>) => {
        setBusy(true)
        try { await action(); await settings.reload() } catch { /* reported on the error bus */ }
        finally { setBusy(false) }
    }

    // Blank to a spinner only on the *initial* load (no data yet). A reload after a save keeps the
    // list rendered — `busy` disables it — instead of flashing the whole panel to a spinner.
    if (data === null) {
        if (settings.error !== null) return <LoadError onRetry={settings.reload}/>
        return <Loading/>
    }

    const mappedByUuid = new Map(data.category_mappings.map(m => [m.category_uuid, m]))
    // One row per Zettle category, plus any stored mapping whose category is no longer offered by
    // Zettle (kept visible/removable), plus a final catch-all "Standard" row for the optional
    // default placement (used when a category has no mapping) — all in the one table.
    type CategoryListRow =
        | { kind: 'category', uuid: string, name: string, mapping: SwishCategoryMapping | null }
        | { kind: 'default', mapping: SwishLocation | null }
    const categoryRows: CategoryListRow[] = [
        ...data.zettle_categories.map(c => ({ kind: 'category' as const, uuid: c.uuid, name: c.name, mapping: mappedByUuid.get(c.uuid) ?? null })),
        ...data.category_mappings
            .filter(m => !data.zettle_categories.some(c => c.uuid === m.category_uuid))
            .map(m => ({ kind: 'category' as const, uuid: m.category_uuid, name: `${m.category_name} (ej i Zettle-listan)`, mapping: m })),
        { kind: 'default', mapping: data.default_location },
    ]

    // The backend gates these three separately (web/swish.py). Without them the controls used to be
    // shown and the save came back 403 -- an error where an absence was meant.
    const mayChangeCategory = (r: CategoryListRow) => r.kind === 'default' ? canDefault : canCategories

    const saveCategory = (uuid: string, categoryName: string, loc: SwishLocation) =>
        run(() => apiPutSwishCategory({ category_uuid: uuid, category_name: categoryName, ...loc }))
    // A changed message on an edit is a rename: delete the old key, then upsert the new — both in
    // one action so the reload sees the final state.
    const saveMessage = (original: SwishMessageMapping | null, message: string, loc: SwishLocation,
                         amountMode: SwishAmountMode, amount: number | null, isBackup: boolean) =>
        run(async () => {
            if (original !== null && original.message !== message) await apiDeleteSwishMessage(original.message)
            await apiPutSwishMessage({ message, ...loc, amount_mode: amountMode, amount,
                                       is_zettle_backup: isBackup })
        })

    return (
        <Box sx={{ p: 1, maxWidth: '60rem' }}>
            {canCategories &&
            <SettingsSection title="Kassans Swish-meddelande"
                description={<>
                    Det fasta meddelandet i kassans Swish-QR-kod, till exempel <em>25manna 2026</em>.
                    Bara betalningar med det meddelandet matchas mot Zettle-köpen. Lämnas rutan tom
                    matchas alla betalningar utan affischmeddelande, även de som inte kommer från kassan.
                </>}>
                <TextField id="swish-pos-message" label="Meddelande" size="small" sx={{ maxWidth: '20rem' }}
                    disabled={busy} defaultValue={data.pos_message ?? ''}
                    placeholder="t.ex. 25manna 2026"
                    onBlur={e => {
                        const value = e.target.value.trim() || null
                        if (value !== (data.pos_message ?? null)) run(() => apiSetSwishPosMessage(value))
                    }}/>
            </SettingsSection>}
            {(canCategories || canDefault) &&
            <SettingsSection title="Zettle-kategorier (POS)"
                description={<>
                    Varje Zettle-kategori bokförs på valt konto. Raden <em>Standard</em> nederst används för intäkt
                    vars kategori saknar mappning (valfritt — utan den flaggas dagen i stället, säkrare än att gissa).
                </>}>
                {data.zettle_categories.length === 0 &&
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Kunde inte hämta Zettle-kategorier. Befintliga mappningar kan ändras nedan; nya kräver att
                        kategorilistan kan läsas från Zettle.
                    </Alert>}
                <MappingList
                    rows={categoryRows}
                    getKey={r => r.kind === 'default' ? 'default' : r.uuid}
                    renderName={r => r.kind === 'default'
                        ? <span style={{ fontStyle: 'italic', color: '#555' }}>Standard (kategori saknas)</span>
                        : r.name}
                    getSummary={r => r.mapping !== null ? summarizeLocation(data, r.mapping) : null}
                    nameHeader="Kategori" locationHeader="Bokförs på"
                    unmappedText={r => r.kind === 'default' ? 'ingen standard' : 'ej mappad'}
                    unmappedActionLabel={r => r.kind === 'default' ? 'Sätt' : 'Mappa'}
                    canRemove={r => mayChangeCategory(r) && r.mapping !== null && inScope(r.mapping)}
                    canEdit={r => mayChangeCategory(r) && (r.mapping === null || inScope(r.mapping))}
                    editDisabledReason={r => mayChangeCategory(r)
                        ? 'Utanför ditt ansvarsområde'
                        : 'Du har inte behörighet till den här inställningen'}
                    onRemove={r => r.kind === 'default'
                        ? run(() => apiDeleteSwishDefault())
                        : run(() => apiDeleteSwishCategory(r.uuid))}
                    addLabel={null}
                    disabled={busy}
                    renderEditor={(editing, close) => {
                        if (editing.mode !== 'edit') return null
                        const r = editing.row
                        return r.kind === 'default'
                            ? <LocationOnlyEditor incomeData={data}
                                heading={<div style={{ fontWeight: 600 }}>Standard – när kategori saknas</div>}
                                initialLoc={fromLocation(r.mapping)} disabled={busy} onCancel={close}
                                onSave={loc => run(() => apiPutSwishDefault(loc)).then(close)}/>
                            : <LocationOnlyEditor incomeData={data}
                                heading={<div style={{ fontWeight: 600 }}>{r.name}</div>}
                                initialLoc={fromLocation(r.mapping)} disabled={busy} onCancel={close}
                                onSave={loc => saveCategory(r.uuid, r.mapping?.category_name ?? r.name, loc).then(close)}/>
                    }}
                />
            </SettingsSection>}

            {canCategories &&
            <SettingsSection title="Presentkort"
                description={<>
                    När ett presentkort löses in mot varor i kassan byter inga pengar ägare. Varorna
                    bokförs som en intern försäljning: intäkten på kategorins egen mappning ovan, och
                    kostnaden här — hos den som delade ut presentkortet. Utfärdandet av presentkort
                    bokförs inte. Utan en kostnad här bokförs inga inlösta presentkort.
                </>}>
                <div id="swish-giftcard-cost">
                <MappingList
                    rows={[{ mapping: data.giftcard_cost_location }]}
                    getKey={() => 'giftcard-cost'}
                    renderName={() => 'Inlösta presentkort'}
                    getSummary={r => r.mapping !== null ? summarizeCostLocation(data, r.mapping) : null}
                    nameHeader="" locationHeader="Kostnaden bokförs på"
                    unmappedText="ej angiven"
                    unmappedActionLabel="Sätt"
                    canRemove={r => r.mapping !== null && inScope(r.mapping)}
                    canEdit={r => r.mapping === null || inScope(r.mapping)}
                    onRemove={() => run(() => apiDeleteSwishGiftcardCost())}
                    addLabel={null}
                    disabled={busy}
                    renderEditor={(editing, close) => editing.mode !== 'edit' ? null
                        : <CostLocationEditor data={data} initial={editing.row.mapping} disabled={busy}
                            onCancel={close}
                            onSave={loc => run(() => apiPutSwishGiftcardCost(loc)).then(close)}/>}
                />
                </div>
            </SettingsSection>}

            {canMessages &&
            <SettingsSection title="Meddelanden (affisch)"
                description={<>
                    Meddelandet är det fasta Swish-meddelandet på affischens QR-kod. Det måste vara så
                    särskiljande att ingen råkar skriva samma sak för ett annat ändamål.
                </>}>
                <MappingList
                    rows={data.message_mappings}
                    getKey={m => m.message}
                    renderName={m => {
                        const w = messageStrength(m.message)
                        return <>{m.message}{w.weak && <span title={w.reason ?? ''} style={{ color: '#b26a00', marginLeft: '0.4rem', cursor: 'help' }}>⚠</span>}</>
                    }}
                    getSummary={m => `${summarizeLocation(data, m)} · ${amountLabel(m)}`}
                    nameHeader="Meddelande" locationHeader="Bokförs på / belopp"
                    canRemove={m => inScope(m)}
                    canEdit={m => inScope(m)}
                    onRemove={m => run(() => apiDeleteSwishMessage(m.message))}
                    addLabel="+ Lägg till meddelande…"
                    disabled={busy}
                    emptyText="Inga meddelanden."
                    renderRowActions={m =>
                        <Tooltip title={data.swish_number_configured ? 'Ladda ner affisch (PDF)' : 'Ange Swish-nummer under Inställningar → Företag för att skapa affisch'}><span>
                            <Button size="small" disabled={busy || !data.swish_number_configured}
                                onClick={() => window.open(swishPosterUrl(m.message), '_blank')}>Affisch</Button>
                        </span></Tooltip>}
                    renderEditor={(editing, close) => {
                        const original = editing.mode === 'edit' ? editing.row : null
                        return <MessageEditor incomeData={data}
                            initialMessage={original?.message ?? ''}
                            initialLoc={original !== null ? fromLocation(original) : EMPTY_LOCATION}
                            initialAmountMode={original?.amount_mode ?? 'open'}
                            initialAmount={original?.amount ?? null}
                            initialIsBackup={original?.is_zettle_backup ?? false}
                            disabled={busy} onCancel={close}
                            onSave={(msg, loc, mode, amt, backup) =>
                                saveMessage(original, msg, loc, mode, amt, backup).then(close)}/>
                    }}
                />
            </SettingsSection>}
        </Box>
    )
}

export default SwishSettings
